<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    columns: {
        type: Array,
        required: true,
        default: () => []
    },
    emptyStateIcon: {
        type: String,
        default: 'mdi-table'
    },
    emptyStateMessage: {
        type: String,
        default: 'No data available'
    },
    searchPlaceholder: {
        type: String,
        default: 'Search...'
    },
    searchable: {
        type: Boolean,
        default: true
    }
});

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortBy = ref(null);
const sortOrder = ref('asc');

const itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 }
];

const handleSort = (column) => {
    if (!column.sortable) return;
    
    if (sortBy.value === column.key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column.key;
        sortOrder.value = 'asc';
    }
};

const filteredData = computed(() => {
    if (!searchQuery.value || !props.searchable) return props.data;
    
    const query = searchQuery.value.toLowerCase();
    return props.data.filter(item => {
        return props.columns.some(column => {
            const value = item[column.key];
            if (value === null || value === undefined) return false;
            return value.toString().toLowerCase().includes(query);
        });
    });
});

const filteredAndSortedData = computed(() => {
    let result = [...filteredData.value];
    
    if (sortBy.value) {
        result.sort((a, b) => {
            let aValue = a[sortBy.value];
            let bValue = b[sortBy.value];
           
            const column = props.columns.find(col => col.key === sortBy.value);
            const isDateField = column?.type === 'date';
            const isNumberField = column?.type === 'number';
            const isTimeField = column?.type === 'time';
            
            if (isDateField) {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            } else if (isNumberField || isTimeField) {
                
                aValue = Number(aValue);
                bValue = Number(bValue);
            }
            
            const multiplier = sortOrder.value === 'asc' ? 1 : -1;
            
            if (!isNumberField && !isTimeField && typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' }) * multiplier;
            }
            
            if (aValue < bValue) return -1 * multiplier;
            if (aValue > bValue) return 1 * multiplier;
            return 0;
        });
    }
    
    return result;
});

const totalItems = computed(() => filteredAndSortedData.value.length);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => startIndex.value + itemsPerPage.value);
const paginatedData = computed(() => {
    return filteredAndSortedData.value.slice(startIndex.value, endIndex.value);
});

const displayedPages = computed(() => {
    if (totalPages.value <= 7) return Array.from({length: totalPages.value}, (_, i) => i + 1);
    
    let pages = [];
    if (currentPage.value <= 3) {
        pages = [1, 2, 3, 4, '...', totalPages.value];
    } else if (currentPage.value >= totalPages.value - 2) {
        pages = [1, '...', totalPages.value - 3, totalPages.value - 2, totalPages.value - 1, totalPages.value];
    } else {
        pages = [1, '...', currentPage.value - 1, currentPage.value, currentPage.value + 1, '...', totalPages.value];
    }
    return pages;
});

const handleItemsPerPageChange = (value) => {
    itemsPerPage.value = Number(value);
    
    if (currentPage.value > totalPages.value) {
        currentPage.value = totalPages.value || 1;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const goToPage = (page) => {
    currentPage.value = page;
};

const getCellValue = (item, column) => {
    const value = item[column.key];
    
    if (column.formatter && typeof column.formatter === 'function') {
        return column.formatter(value, item);
    }
    
    return value;
};
</script>

<template>
    <div class="w-full h-full">
        <!-- Empty State -->
        <div v-if="!data.length" class="py-12 h-full flex flex-col items-center justify-center text-white/80">
            <i :class="`${emptyStateIcon} text-6xl`"></i>
            <p class="text-lg mt-4">{{ emptyStateMessage }}</p>
        </div>

        <!-- Table View -->
        <div v-else class="block">
            <!-- Pagination and Search Section -->
            <div class="flex items-center px-4 py-2 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-between space-x-4 w-full">
                    <div class="flex items-center space-x-4">
                        <div class="text-xs text-white/80"> 
                            Showing {{ startIndex + 1 }}-{{ Math.min(endIndex, totalItems) }} of {{ totalItems }} items
                        </div>
                        <div class="flex items-center space-x-2 border-l border-slate-600/50 pl-4">
                            <label for="itemsPerPage" class="text-xs text-white/80">Items per page:</label>
                            <select
                                id="itemsPerPage"
                                :value="itemsPerPage"
                                @change="handleItemsPerPageChange($event.target.value)"
                                class="bg-echoes-background-black border border-echoes-border-gray text-xs rounded-lg focus:ring-echoes-primary focus:border-echoes-primary p-1 dark:text-white appearance-none text-center"
                            >
                                <option
                                    v-for="option in itemsPerPageOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div v-if="searchable" class="flex items-center w-[250px]">
                        <input
                            type="text"
                            v-model="searchQuery"
                            :placeholder="searchPlaceholder"
                            class="truncate w-full p-3 rounded-[8px] h-8 flex items-center text-xs
                                bg-echoes-background-black text-white/80
                                border border-echoes-border-gray
                                placeholder-white/40
                                transition-all duration-200 ease-in-out
                                focus:outline-none focus:ring-2 focus:ring-echoes-primary focus:border-echoes-primary
                                hover:bg-echoes-background-black/90 hover:border-echoes-border-gray"
                        />
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="border border-t-0 border-l-0 border-r-0 border-echoes-border-gray overflow-x-auto">
                <table class="w-full border-collapse text-sm">
                    <thead class="bg-echoes-background-black">
                        <tr class="border-b border-echoes-border-gray">
                            <th 
                                v-for="column in columns" 
                                :key="column.key"
                                @click="handleSort(column)"
                                class="px-4 py-3 2xl:py-5 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap"
                                :class="[
                                    column.headerClass,
                                    column.sortable ? 'cursor-pointer hover:text-white select-none' : ''
                                ]"
                            >
                                <div class="flex items-center gap-2">
                                    <span>{{ column.label }}</span>
                                    <span v-if="column.sortable" class="flex flex-col">
                                        <i 
                                            v-if="sortBy === column.key && sortOrder === 'asc'"
                                            class="mdi mdi-arrow-up text-echoes-primary"
                                        ></i>
                                        <i 
                                            v-else-if="sortBy === column.key && sortOrder === 'desc'"
                                            class="mdi mdi-arrow-down text-echoes-primary"
                                        ></i>
                                        <i 
                                            v-else
                                            class="mdi mdi-unfold-more-horizontal text-white/40"
                                        ></i>
                                    </span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-transparent divide-y divide-echoes-border-gray">
                        <tr v-for="(item, index) in paginatedData" :key="index"
                            class="hover:bg-echoes-background-black/30 transition-colors duration-200">
                            <td 
                                v-for="column in columns" 
                                :key="column.key"
                                class="px-4 py-6 text-sm text-white/80"
                                :class="column.cellClass"
                            >
                                <slot 
                                    :name="`cell-${column.key}`" 
                                    :item="item" 
                                    :value="item[column.key]"
                                >
                                    <span :class="column.valueClass">
                                        {{ getCellValue(item, column) }}
                                    </span>
                                </slot>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination Buttons -->
            <div class="flex items-center px-4 py-2 2xl:py-4 bg-echoes-background-black border border-echoes-border-gray border-t-0 border-l-0 border-r-0">
                <div class="flex items-center justify-center w-full">
                    <div class="flex items-center space-x-2">
                        <button @click="prevPage" :disabled="currentPage === 1" 
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === 1
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-left text-lg"></i>
                        </button>
                        
                        <template v-for="page in displayedPages" :key="page">
                            <span v-if="page === '...'" class="px-3 py-1 text-sm text-white/80">...</span>
                            <button v-else @click="goToPage(page)"
                                class="px-3 py-1 text-sm rounded-[7px] transition-colors duration-200"
                                :class="[
                                    currentPage === page
                                        ? 'bg-echoes-primary text-white hover:bg-echoes-primary/80'
                                        : 'text-white/80 hover:bg-echoes-background-black/30'
                                ]">
                                {{ page }}
                            </button>
                        </template>

                        <button @click="nextPage" :disabled="currentPage === totalPages"
                            class="text-sm rounded-[13px] transition-colors duration-200"
                            :class="[
                                currentPage === totalPages
                                    ? 'text-white/80 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-right text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
/* Add any additional styles if needed */
</style>

